import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { ResponsiveLine } from '@nivo/line';
import { Dropdown, Grid, Segment } from 'semantic-ui-react';
import currencyFormatter from 'currency-formatter';
import styles from './PaymentsGraph.scss';

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const quarters = [
  'Q1',
  'Q2',
  'Q3',
  'Q4'
];

export default class PaymentsGraph extends React.Component {
  static propTypes = {
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      revenueYear: moment.tz(this.props.viewer.s_time_zone).year()
    };
    this.onRevenueYearChange = this.onRevenueYearChange.bind(this);
    this.dateField = this.dateField.bind(this);
    this.totalOutstandingPayments = this.totalOutstandingPayments.bind(this);
    this.totalCompletedPayments = this.totalCompletedPayments.bind(this);
    this.totalRefunds = this.totalRefunds.bind(this);
    this.totalRevenue = this.totalRevenue.bind(this);
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
  }

  componentWillMount() {
    this.initGraphData();
    this.updateWindowDimensions();
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  onRevenueYearChange(e, { value }) {
    this.setState({ revenueYear: value });
    const startTime = moment.tz(value, 'Y', this.timeZone())
      .startOf('year')
      .toISOString();
    const endTime = moment.tz(value, 'Y', this.timeZone())
      .endOf('year')
      .toISOString();
    this.props.relay.refetch(
      {
        completed_payments_before: endTime,
        completed_payments_after: startTime,
        outstanding_payments_before: endTime,
        outstanding_payments_after: startTime,
        refunds_before: endTime,
        refunds_after: startTime
      },
      null,
      () => {
        this.initGraphData();
      }
    );
  }

  groupByQuarter(payments, type) {
    const byQuarter = {};
    const byMonth = this.groupByMonth(
      payments,
      type
    );
    byQuarter.Q1 = byMonth.Jan + byMonth.Feb + byMonth.Mar;
    byQuarter.Q2 = byMonth.Apr + byMonth.May + byMonth.Jun;
    byQuarter.Q3 = byMonth.Jul + byMonth.Aug + byMonth.Sep;
    byQuarter.Q4 = byMonth.Oct + byMonth.Nov + byMonth.Dec;
    return byQuarter;
  }

  groupByMonth(payments, type) {
    const byMonth = {};
    months.map(month => {
      byMonth[month] = 0;
      return month;
    });
    payments.map(payment => {
      const month = moment.tz(payment.node[this.dateField(type)], this.timeZone()).format('MMM');
      byMonth[month] = byMonth[month] + payment.node.amount_cents;
      return payment;
    });
    return byMonth;
  }

  dateField(type) {
    switch (type) {
      case 'outstanding':
        return 'payment_date';
      case 'completed':
        return 'paid_at';
      case 'refunds':
        return 'refunded_at';
      default:
        return null;
    }
  }

  initGraphData() {
    const completedByMonth = this.groupByMonth(
      this.props.viewer.completed_payments.edges,
      'completed'
    );
    const outstandingByMonth = this.groupByMonth(
      this.props.viewer.outstanding_payments.edges,
      'outstanding'
    );
    const refundsByMonth = this.groupByMonth(
      this.props.viewer.refunds.edges,
      'refunds'
    );
    const completedByQuarter = this.groupByQuarter(
      this.props.viewer.completed_payments.edges,
      'completed'
    );
    const outstandingByQuarter = this.groupByQuarter(
      this.props.viewer.outstanding_payments.edges,
      'outstanding'
    );
    const refundsByQuarter = this.groupByQuarter(
      this.props.viewer.refunds.edges,
      'refunds'
    );
    const graphDataByQuarter = [
      {
        id: 'Paid',
        color: '#547687',
        data: quarters.map(quarter => ({ x: quarter, y: (completedByQuarter[quarter] - refundsByQuarter[quarter]) / 100 }))
      },
      {
        id: 'Outstanding',
        color: '#7f98a5',
        data: quarters.map(quarter => ({ x: quarter, y: outstandingByQuarter[quarter] / 100 }))
      }
    ];
    const graphDataByMonth = [
      {
        id: 'Paid',
        color: '#547687',
        data: months.map(month => ({ x: month, y: (completedByMonth[month] - refundsByMonth[month]) / 100 }))
      },
      {
        id: 'Outstanding',
        color: '#7f98a5',
        data: months.map(month => ({ x: month, y: outstandingByMonth[month] / 100 }))
      }
    ];
    this.setState({ graphDataByMonth, graphDataByQuarter });
  }

  timeZone() {
    return this.props.viewer.s_time_zone;
  }

  totalCompletedPayments() {
    return this.props.viewer.completed_payments.edges.reduce((acc, p) => acc + p.node.amount_cents, 0);
  }

  totalOutstandingPayments() {
    return this.props.viewer.outstanding_payments.edges.reduce((acc, p) => acc + p.node.amount_cents, 0);
  }

  totalRefunds() {
    return this.props.viewer.refunds.edges.reduce((acc, r) => acc + r.node.amount_cents, 0);
  }

  totalRevenue() {
    return this.totalCompletedPayments() + this.totalOutstandingPayments() - this.totalRefunds();
  }

  updateWindowDimensions() {
    this.setState({
      window: { width: window.innerWidth, height: window.innerHeight }
    });
  }

  render() {
    const currency = 'USD';
    const tooltip = ({ data }) => {
      const outstanding = data[0].data.y;
      const paid = data[1].data.y;
      const total = outstanding + paid;
      return (
        <table>
          <tbody>
            <tr>
              <td>
                <span className={styles.outstandingLegend} />
                Outstanding
              </td>
              <td className={styles.tooltipNumber}>
                {currencyFormatter.format(outstanding, { code: currency })}
              </td>
            </tr>
            <tr>
              <td>
                <span className={styles.paidLegend} />
                Paid
              </td>
              <td className={styles.tooltipNumber}>
                {currencyFormatter.format(paid, { code: currency })}
              </td>
            </tr>
            <tr>
              <td>
                <span className={styles.legendLabel} />
                <strong>Total Sales</strong>
              </td>
              <td className={styles.tooltipNumber}>
                <strong>{currencyFormatter.format(total, { code: currency })}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      );
    };
    const years = [
      { text: '2014', value: 2014 },
      { text: '2015', value: 2015 },
      { text: '2016', value: 2016 },
      { text: '2017', value: 2017 },
      { text: '2018', value: 2018 },
      { text: '2019', value: 2019 },
      { text: '2020', value: 2020 },
      { text: '2021', value: 2021 },
      { text: '2022', value: 2022 },
      { text: '2023', value: 2023 }
    ];
    const theme = {
      axis: {
        ticks: {
          text: {
            fontSize: 14
          }
        }
      }
    };
    const isLargeScreen = this.state.window.width >= 1280;
    const dateRange = <strong>Date Range &nbsp;</strong>;
    return (
      <Grid stackable>
        <Grid.Row columns='equal'>
          <Grid.Column>
            <Segment style={{ background: 'white' }}>
              <div style={isLargeScreen ? { textAlign: 'right' } : {}}>
                {isLargeScreen && dateRange}
                {!isLargeScreen && <p style={{ marginBottom: '0.5em' }}>{dateRange}</p>}
                <Dropdown
                  fluid={!isLargeScreen}
                  options={years}
                  basic
                  button
                  floating
                  value={this.state.revenueYear}
                  onChange={this.onRevenueYearChange}
                />
              </div>
              <div style={{ height: 400 }}>
                <ResponsiveLine
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 60,
                    left: 80
                  }}
                  axisLeft={{
                    format: v => currencyFormatter.format(v, { code: currency })
                  }}
                  tooltip={tooltip}
                  data={isLargeScreen ? this.state.graphDataByMonth : this.state.graphDataByQuarter}
                  animate
                  enableArea
                  enableGridY={false}
                  enableGridX={false}
                  yScale={{ type: 'linear', stacked: true }}
                  curve='monotoneX'
                  colorBy={d => d.color}
                  dotSize={8}
                  dotColor='#fff'
                  dotBorderWidth={2}
                  theme={theme}
                />
              </div>
              <div style={{ marginLeft: '80px' }}>
                <Grid stackable container>
                  <Grid.Row columns='equal'>
                    <Grid.Column>
                      <div className={styles.legend}>
                        <div>
                          <span className={styles.outstandingLegend} />
                          Outstanding
                        </div>
                        <div>
                          <span className={styles.paidLegend} />
                          Paid
                        </div>
                      </div>
                    </Grid.Column>
                    <Grid.Column>
                      <p className={styles.total}>
                        {currencyFormatter.format(this.totalRevenue() / 100, { code: currency })}
                      </p>
                      <h3 className={styles.totalLabel}>Total Sales</h3>
                    </Grid.Column>
                    <Grid.Column>
                      <p className={styles.total}>
                        {currencyFormatter.format((this.totalCompletedPayments() - this.totalRefunds()) / 100, { code: currency })}
                      </p>
                      <h3 className={styles.totalLabel}>Total Paid</h3>
                    </Grid.Column>
                    <Grid.Column>
                      <p className={styles.total}>
                        {currencyFormatter.format(this.totalOutstandingPayments() / 100, { code: currency })}
                      </p>
                      <h3 className={styles.totalLabel}>Outstanding Payments</h3>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </div>
            </Segment>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Financial/PaymentsGraph/PaymentsGraph.js